import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useGroupSessions } from '../groupSession/hooks';
import { normalizeSmartScheduleData } from '../smartSchedule/normalize';
import { sortSmartScheduleEvents } from '../smartSchedule/helpers';
import type { SmartScheduleEvent } from '../smartSchedule/types';
import { fetchSmartRangeSchedules, smartRangeSchedulesQueryKey } from './queries';

export interface UseQuickMoveDayPreviewParams {
    centerId: string;
    authorization: string;
    /** 목적지 KST 날짜 'YYYY-MM-DD'. */
    date: string;
    /** 시트의 강사(고정). 이 강사의 일정만 돌려준다. */
    coachId: string;
    /** 시트가 열려 있을 때만 true. 전역 기본값이 enabled:false 라 반드시 넘긴다. */
    enabled: boolean;
    /** 옮기려는 원본 일정 id — 미리보기에서 빼고 싶을 때. */
    excludeScheduleId?: string;
}

export interface UseQuickMoveDayPreviewReturn {
    /** 그 날짜·그 강사의 일정(필터 적용 전 데이터: PT·일반일정·OFF·신청 대기·그룹 포함). 시작 시각 순. */
    events: SmartScheduleEvent[];
    isLoading: boolean;
    isFetching: boolean;
    /** 기본 일정(범위 조회) 실패 — 미리보기를 믿을 수 없다. */
    isError: boolean;
    /** 그룹수업만 실패 — 기본 일정은 보이지만 그룹수업이 빠졌을 수 있다. */
    isGroupError: boolean;
    refetch: () => void;
}

/**
 * 목적지 날짜의 그 강사 일정 미리보기(§3). 다른 날을 추측으로 고르지 않게 한다.
 *
 * 「한눈에 › 일간」과 **같은 쿼리 키**(Smart 범위 조회 [date,date] + 그룹수업 목록)를 써서 캐시를 공유한다.
 * 화면의 강사·종류·상태 필터와 무관한 원본 데이터다. 신규기회는 센터 기능 설정에 따라 달라 포함하지 않는다.
 */
export const useQuickMoveDayPreview = ({
    centerId,
    authorization,
    date,
    coachId,
    enabled,
    excludeScheduleId,
}: UseQuickMoveDayPreviewParams): UseQuickMoveDayPreviewReturn => {
    const isEnabled = enabled && !!centerId && !!authorization && !!date;

    const rangeQuery = useQuery({
        queryKey: smartRangeSchedulesQueryKey(centerId, date, date),
        queryFn: () => fetchSmartRangeSchedules(centerId, date, date, authorization),
        enabled: isEnabled,
        staleTime: 60 * 1000,
    });

    const groupQuery = useGroupSessions({
        centerId,
        startDate: date,
        endDate: date,
        enabled: isEnabled,
    });

    const events = useMemo(() => {
        const normalized = normalizeSmartScheduleData({
            rangeSchedules: rangeQuery.data || [],
            groupSessions: groupQuery.data || [],
        }).events;
        return sortSmartScheduleEvents(
            normalized.filter((event) => event.coachId === coachId && event.id !== excludeScheduleId)
        );
    }, [coachId, excludeScheduleId, groupQuery.data, rangeQuery.data]);

    const { refetch: refetchRange } = rangeQuery;
    const { refetch: refetchGroup } = groupQuery;

    return {
        events,
        isLoading: isEnabled && (rangeQuery.isLoading || groupQuery.isLoading),
        isFetching: isEnabled && (rangeQuery.isFetching || groupQuery.isFetching),
        isError: isEnabled && rangeQuery.isError,
        isGroupError: isEnabled && groupQuery.isError,
        refetch: () => {
            void refetchRange();
            void refetchGroup();
        },
    };
};
